import Feature from 'ol/Feature';
import Polygon, { fromCircle } from 'ol/geom/Polygon';
import { getCenter } from 'ol/extent';

import { pushHistory } from '@utils/map/annotation/boxHistory';

/**
 * Pure helpers over the live OpenLayers source of an object annotation
 * session. A box belongs to an object through `nodeUuid` and to a tile through
 * `tileName` (both set at draw time or when the list is read); `saved` marks it
 * as kept in the session store. Detections loaded from a model carry none of
 * these and are left alone.
 *
 * A box's `source` is `'human'` (the default: drawn here, or an `add` / `edit`
 * row of the list) or `'model'` (a box of the object's model). A model box keeps
 * its original shape in `modelGeometry`; a reloaded `edit` row keeps the model
 * shape it corrects in `targetGeometry`. Both are GeoJSON geometries in the
 * projection the list was read in, sent back as they came.
 */

const isModelBox = feature => feature.get('source') === 'model';

const belongsTo = (feature, nodeUuid) => feature.get('nodeUuid') === nodeUuid;

/** The boxes of `nodeUuid` in `tileName` (every tile when `tileName` is omitted). */
export const boxesOf = (source, nodeUuid, tileName) =>
	source
		.getFeatures()
		.filter(
			feature =>
				belongsTo(feature, nodeUuid) &&
				(tileName === undefined || feature.get('tileName') === tileName)
		);

/**
 * Whether the tile differs from what the session saved for `nodeUuid` in it: a
 * box that is new or moved (unsaved), or a saved box that was removed. Boxes
 * match by `boxId`, which the clones in the store keep. A model box the user
 * moved or reshaped is unsaved like any other; a model box the user erased is a
 * pending reject.
 */
export const hasChanges = (source, nodeUuid, tileName, saved = [], rejects = []) => {
	const live = boxesOf(source, nodeUuid, tileName);
	if (live.some(feature => !feature.get('saved'))) return true;
	if (rejects.length) return true;
	const liveIds = new Set(live.map(feature => feature.get('boxId')));
	return saved.some(feature => !liveIds.has(feature.get('boxId')));
};

/**
 * Keep the boxes of `nodeUuid` in `tileName`: every live box is marked saved
 * and clones of the human ones are returned for the store. Model boxes are
 * server state and stay out of it.
 */
export const takeSnapshot = (source, nodeUuid, tileName) =>
	boxesOf(source, nodeUuid, tileName)
		.map(feature => {
			feature.set('saved', true, true);
			return isModelBox(feature) ? null : feature.clone();
		})
		.filter(Boolean);

/** Put stored boxes back on the map, as fresh live copies, already saved. */
export const restoreBoxes = (source, stored) => {
	const copies = stored.map(feature => {
		const copy = feature.clone();
		copy.set('saved', true, true);
		return copy;
	});
	if (copies.length) source.addFeatures(copies);
	return copies;
};

/**
 * The position of the tile named \`tileName\` in \`tiles\`, or 0 (the first) when it
 * is not among them; where a session opens.
 */
export const indexOfTile = (tiles = [], tileName) =>
	Math.max(
		0,
		tiles.findIndex(tile => tile?.properties?.tile_name === tileName)
	);

/**
 * The tile of `tiles` (GeoJSON Polygon features in map coordinates, named by
 * `properties.tile_name`) that holds the centre of `box`, or undefined. Tiles
 * can overlap (a grid holds tiles of more than one offset), so when `preferred`
 * names one that holds the box, it wins over the others.
 */
export const tileContaining = (tiles = [], box, preferred) => {
	const geometry = box?.getGeometry?.();
	if (!geometry) return undefined;
	const centre = getCenter(geometry.getExtent());
	const holds = candidate => {
		const rings = candidate?.geometry?.coordinates;
		return Boolean(rings && new Polygon(rings).intersectsCoordinate(centre));
	};
	const favourite = preferred && tiles.find(tile => tile?.properties?.tile_name === preferred);
	if (favourite && holds(favourite)) return preferred;
	return tiles.find(holds)?.properties?.tile_name;
};

/**
 * The area to read one tile's rows with: the tile's square widened by its own
 * width and height on every side (a 3 × 3 tile block), closed. An orthophoto read
 * matches a row by the centre of its geometry while a save replaces the rows by
 * `tile_name`, so a box filed under the tile but hanging over its edge must still
 * come back; the caller keeps the tile's rows by `tile_name`.
 *
 * @param {number[][]} ring - the tile's ring in map units
 * @returns {number[][]|undefined}
 */
export const tileReadRing = ring => {
	if (!Array.isArray(ring) || ring.length < 3) return undefined;
	const xs = ring.map(([x]) => x);
	const ys = ring.map(([, y]) => y);
	const [minX, maxX, minY, maxY] = [
		Math.min(...xs),
		Math.max(...xs),
		Math.min(...ys),
		Math.max(...ys),
	];
	const [width, height] = [maxX - minX, maxY - minY];
	const [left, right, bottom, top] = [minX - width, maxX + width, minY - height, maxY + height];
	return [
		[left, bottom],
		[left, top],
		[right, top],
		[right, bottom],
		[left, bottom],
	];
};

/** The distinct labels a set of boxes carry, in first-seen order; the default (null) is left out. */
export const labelsOn = (features = []) => [
	...new Set(features.map(feature => feature.get('label')).filter(Boolean)),
];

/**
 * Give one box another label (null = the object's default). The box is unsaved
 * again, so the tile's Save picks the change up. Returns false when the box is
 * not on the map.
 */
export const relabelBox = (source, boxId, label) => {
	const box = source.getFeatures().find(feature => feature.get('boxId') === boxId);
	if (!box) return false;
	const previousLabel = box.get('label') ?? null;
	const wasSaved = box.get('saved');
	if (previousLabel === (label ?? null)) return true;
	box.set('label', label ?? null, true);
	box.set('saved', false);
	pushHistory({
		undo: () => {
			box.set('label', previousLabel, true);
			box.set('saved', wasSaved);
		},
	});
	return true;
};

const ANNOTATION_TYPES = ['bbox', 'rotated_bbox', 'polygon', 'circle'];

/** A closed box ring has five points; one with more is a polygon's. */
const isPolygonRing = ring => (ring?.length ?? 0) > 5;

/** The `annotation_type` a box is sent as: circle, rotated box, polygon or plain box. */
export const annotationTypeOf = feature => {
	const geometry = feature.getGeometry();
	if (geometry?.getType() === 'Circle') return 'circle';
	const stored = feature.get('annotation_type');
	if (ANNOTATION_TYPES.includes(stored)) return stored;
	return isPolygonRing(geometry?.getCoordinates?.()[0]) ? 'polygon' : 'bbox';
};

/** A closed GeoJSON Polygon from a ring (the first point is repeated when it is not already). */
const closedPolygon = ring => {
	const first = ring[0];
	const last = ring[ring.length - 1];
	const closed = first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];
	return { type: 'Polygon', coordinates: [closed] };
};

/** The outer ring of a box in map coordinates (a circle is polygonised first). */
const ringOf = feature => {
	const geometry = feature.getGeometry();
	const polygon = geometry.getType() === 'Circle' ? fromCircle(geometry) : geometry;
	return polygon.getCoordinates()[0];
};

/** Whether a save of these boxes would send any row (else it is an empty mark). */
export const tileHasRows = (boxes, rejects = [], storedRejects = []) =>
	rejects.length > 0 ||
	storedRejects.length > 0 ||
	boxes.some(box => !isModelBox(box) || !box.get('saved'));

/**
 * The `annotations` of the tile save for one object in one tile.
 *
 * - every human box: `add`, or `edit` (carrying `target_geometry`) when it was
 *   reloaded as an edit row;
 * - every model box the user moved or reshaped (unsaved): `edit` with the new
 *   shape and the model box as `target_geometry`;
 * - every model box the user erased: `reject` with the model box as `geometry`;
 * - every reject saved on the tile before (`storedRejects`), sent again.
 *
 * Untouched model boxes are not rows. No rows at all is an empty mark.
 *
 * @param {object[]} boxes - the live boxes of the object in the tile
 * @param {object[]} [rejects] - the model boxes erased in the tile
 * @param {(ring: number[][]) => number[][]} [toRing] - map ring to request projection
 * @param {{ annotation_type: string, geometry: object }[]} [storedRejects] - the tile's saved rejects
 */
export const tileSavePayload = (boxes, rejects = [], toRing = ring => ring, storedRejects = []) => {
	const rows = [];
	boxes.forEach(box => {
		const label = box.get('label') || null;
		const geometry = closedPolygon(toRing(ringOf(box)));
		const annotation_type = annotationTypeOf(box);
		if (isModelBox(box)) {
			if (box.get('saved')) return;
			rows.push({
				action: 'edit',
				annotation_type,
				geometry,
				label,
				target_geometry: box.get('modelGeometry'),
			});
			return;
		}
		const target = box.get('targetGeometry');
		rows.push(
			target
				? { action: 'edit', annotation_type, geometry, label, target_geometry: target }
				: { action: 'add', annotation_type, geometry, label }
		);
	});
	rejects.forEach(box => {
		// An erased correction rejects the model box it corrected, with that box's shape.
		const target = box.get('modelGeometry') ? null : box.get('targetGeometry');
		const isPolygon = target
			? isPolygonRing(target.coordinates?.[0])
			: box.get('annotation_type') === 'polygon';
		rows.push({
			action: 'reject',
			annotation_type: isPolygon ? 'polygon' : 'bbox',
			geometry: target ?? box.get('modelGeometry'),
		});
	});
	// Rejects saved earlier on this tile: a save replaces the tile's rows, so they
	// travel again or the model boxes they hide come back.
	storedRejects.forEach(({ annotation_type, geometry }) => {
		rows.push({ action: 'reject', annotation_type, geometry });
	});
	return rows;
};

/**
 * Boxes from the list's features (or the whole FeatureCollection).
 *
 * `human` `add` / `edit` rows and `model` boxes become boxes of `nodeUuid` in
 * `color`; background rows only check their tile (`emptyTiles`); `reject` rows
 * draw nothing and are returned as `rejected` (tile, type and geometry). `toCoords(rings, tileName)` maps GeoJSON coordinates of the list
 * (the request projection) to map coordinates.
 *
 * @returns {{ boxes: Feature[], emptyTiles: string[], rejected: object[], status: string|undefined }}
 */
export const boxesFromFeatures = (
	features,
	{ nodeUuid, color, toCoords = coords => coords } = {}
) => {
	const list = Array.isArray(features) ? features : (features?.features ?? []);
	const boxes = [];
	const emptyTiles = new Set();
	const rejected = [];

	list.forEach(item => {
		const props = item.properties ?? {};
		const tileName = props.tile_name;
		if (props.annotation_type === 'background') {
			if (tileName) emptyTiles.add(tileName);
			return;
		}
		// A stored reject draws nothing, but a later save of its tile must send it again.
		if (props.action === 'reject') {
			if (tileName && item.geometry?.coordinates) {
				rejected.push({
					tileName,
					annotation_type: props.annotation_type === 'polygon' ? 'polygon' : 'bbox',
					geometry: item.geometry,
				});
			}
			return;
		}
		const isModel = props.source === 'model';
		if (!isModel && props.action !== 'add' && props.action !== 'edit') return;
		if (!item.geometry?.coordinates) return;

		const box = new Feature({
			geometry: new Polygon(toCoords(item.geometry.coordinates, tileName)),
			nodeUuid,
			nodeColor: color,
			boxId: item.id,
			tileName,
			label: props.label ?? null,
			saved: true,
			source: isModel ? 'model' : 'human',
			isInside: true,
		});
		if (isModel) {
			box.set('modelGeometry', item.geometry, true);
			box.set(
				'annotation_type',
				isPolygonRing(item.geometry.coordinates[0]) ? 'polygon' : 'bbox',
				true
			);
		} else {
			box.set('annotation_type', props.annotation_type ?? 'bbox', true);
			if (props.action === 'edit' && props.target_geometry) {
				box.set('targetGeometry', props.target_geometry, true);
			}
		}
		boxes.push(box);
	});

	return { boxes, emptyTiles: [...emptyTiles], rejected, status: features?.status };
};
